import React, { useState, useEffect, useCallback } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";
import { FaBed, FaBath, FaCar, FaRulerCombined, FaCalendarAlt, FaHome } from "react-icons/fa";

const GOOGLE_MAPS_API_KEY = "AIzaSyCtn44ogwI_q0TGiuwC1X_sOXYijuEdr5M";
const MAP_ID = "1ae451cd191c1a06";

function formatCurrency(val) {
  if (val == null || val === "") return "";
  return "$" + Number(val).toLocaleString();
}

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const scoreColor = (score) => {
  if (score >= 80) return { bg: '#dcfce7', text: '#166534', ring: '#22c55e' };
  if (score >= 60) return { bg: '#fef9c3', text: '#854d0e', ring: '#eab308' };
  return { bg: '#fee2e2', text: '#991b1b', ring: '#ef4444' };
};

// ─── Subject Property Marker ─────────────────────────────────────────────────
const SubjectMarker = ({ position, address, price, photo, bedrooms, bathrooms, carspaces, landSize, isActive, onClick, onClose }) => {
  const [markerRef, marker] = useAdvancedMarkerRef();

  return (
    <>
      <AdvancedMarker ref={markerRef} position={position} onClick={onClick} zIndex={100}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#1a1a1a', color: '#fff',
          borderRadius: 28, padding: '10px 18px 10px 14px',
          fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
          border: '3px solid #fff',
          cursor: 'pointer',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FaHome size={13} color="#fff" />
          </div>
          Your Property
        </div>
        <div style={{
          width: 0, height: 0,
          borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
          borderTop: '8px solid #1a1a1a',
          margin: '0 auto',
        }} />
      </AdvancedMarker>

      {isActive && (
        <InfoWindow anchor={marker} onClose={onClose} maxWidth={320}>
          <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', width: 280 }}>
            {photo && (
              <img
                src={photo}
                alt={address || 'Property'}
                style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 12, display: 'block' }}
              />
            )}
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 6 }}>
              {address || "Subject Property"}
            </div>
            {price && (
              <div style={{ fontWeight: 700, fontSize: 16, color: '#6366f1', marginBottom: 8 }}>
                {formatCurrency(price)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
              {bedrooms != null && bedrooms !== '' && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaBed size={11} /> {bedrooms}</span>}
              {bathrooms != null && bathrooms !== '' && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaBath size={11} /> {bathrooms}</span>}
              {carspaces != null && carspaces !== '' && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaCar size={11} /> {carspaces}</span>}
              {landSize != null && landSize !== '' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <FaRulerCombined size={10} /> {landSize}m²
                </span>
              )}
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

// ─── Comparable Property Marker (card-style) ─────────────────────────────────
const ComparableMarker = ({ position, property, accentColor, dateLabel, isActive, onClick, onClose }) => {
  const [markerRef, marker] = useAdvancedMarkerRef();

  const dateStr = property.filter_date ? formatDate(property.filter_date) : null;
  const score = property.ai_analysis?.score;
  const sc = score != null ? scoreColor(score) : null;

  return (
    <>
      <AdvancedMarker ref={markerRef} position={position} onClick={onClick} zIndex={1}>
        <div style={{ cursor: 'pointer', position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fff', borderRadius: 14,
            padding: 5, paddingRight: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e5e7eb',
            minWidth: 120, maxWidth: 220,
          }}>
            {property.photo_url_main ? (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={property.photo_url_main}
                  alt=""
                  style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', display: 'block' }}
                />
              </div>
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaHome size={16} color="#9ca3af" />
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', gap: 6, fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                {property.bedrooms != null && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><FaBed size={9} />{property.bedrooms}</span>}
                {property.bathrooms != null && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><FaBath size={9} />{property.bathrooms}</span>}
                {property.carspaces != null && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><FaCar size={9} />{property.carspaces}</span>}
              </div>
              {dateStr && (
                <div style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dateLabel} {dateStr}
                </div>
              )}
            </div>
          </div>
          <div style={{
            width: 0, height: 0,
            borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
            borderTop: '6px solid #fff',
            margin: '0 auto',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
          }} />
        </div>
      </AdvancedMarker>

      {isActive && (
        <InfoWindow anchor={marker} onClose={onClose} maxWidth={320}>
          <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', width: 280 }}>
            {property.photo_url_main && (
              <img
                src={property.photo_url_main}
                alt={property.display_address || 'Property'}
                style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 12, display: 'block' }}
              />
            )}
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 6 }}>
              {property.display_address || 'Property'}
            </div>
            {property.price != null && (
              <div style={{ fontWeight: 700, fontSize: 16, color: accentColor, marginBottom: 8 }}>
                {dateLabel === "Rented" ? `$${Number(property.price).toLocaleString()}/wk` : formatCurrency(property.price)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              {property.bedrooms != null && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaBed size={11} /> {property.bedrooms}</span>}
              {property.bathrooms != null && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaBath size={11} /> {property.bathrooms}</span>}
              {property.carspaces != null && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FaCar size={11} /> {property.carspaces}</span>}
              {property.land_area != null && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <FaRulerCombined size={10} />
                  {typeof property.land_area === 'object' ? `${property.land_area.size}${property.land_area.unit}` : `${property.land_area}m²`}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, marginBottom: 4 }}>
              {property.property_type_display && (
                <span style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 10, fontWeight: 500 }}>
                  {property.property_type_display}
                </span>
              )}
              {dateStr && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3, backgroundColor: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 10, fontWeight: 500 }}>
                  <FaCalendarAlt size={9} /> {dateLabel}: {dateStr}
                </span>
              )}
              {property.days_on_market != null && (
                <span style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 10, fontWeight: 500 }}>
                  {property.days_on_market} days
                </span>
              )}
            </div>
            {sc && (
              <div style={{
                marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
                backgroundColor: sc.bg, color: sc.text,
                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
              }}>
                {score}% match
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default function AppraisalMap({
  subjectAddress,
  subjectLat,
  subjectLng,
  subjectPrice,
  subjectPhoto,
  subjectBedrooms,
  subjectBathrooms,
  subjectCarspaces,
  subjectLandSize,
  comparables = [],
  selectedComparables = {},
  rentalComparables = [],
  selectedRentalComparables = {},
  analysisResults = {},
  isMobile,
  onCameraChanged,
}) {
  const [activeMarker, setActiveMarker] = useState(null);

  const saleComps = comparables.filter(c => selectedComparables[c.id] && c.latitude && c.longitude)
    .map(c => ({ ...c, ai_analysis: analysisResults[c.id] || c.ai_analysis }));
  const rentComps = rentalComparables.filter(c => selectedRentalComparables[c.id] && c.latitude && c.longitude)
    .map(c => ({ ...c, ai_analysis: analysisResults[c.id] || c.ai_analysis }));

  const hasSubject = subjectLat && subjectLng;
  const allPoints = [
    ...(hasSubject ? [{ lat: Number(subjectLat), lng: Number(subjectLng) }] : []),
    ...saleComps.map(c => ({ lat: Number(c.latitude), lng: Number(c.longitude) })),
    ...rentComps.map(c => ({ lat: Number(c.latitude), lng: Number(c.longitude) })),
  ];

  if (allPoints.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400, color: "#9ca3af", fontSize: 14 }}>
        No location data available. Select an address and comparables with coordinates to view the map.
      </div>
    );
  }

  const center = {
    lat: allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length,
    lng: allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length,
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} version="beta">
      <Map
        mapId={MAP_ID}
        defaultCenter={center}
        defaultZoom={15}
        style={{ width: "100%", height: isMobile ? 400 : 500, borderRadius: 12 }}
        onCameraChanged={onCameraChanged}
      >
        {hasSubject && (
          <SubjectMarker
            position={{ lat: Number(subjectLat), lng: Number(subjectLng) }}
            address={subjectAddress}
            price={subjectPrice}
            photo={subjectPhoto}
            bedrooms={subjectBedrooms}
            bathrooms={subjectBathrooms}
            carspaces={subjectCarspaces}
            landSize={subjectLandSize}
            isActive={activeMarker === "subject"}
            onClick={() => setActiveMarker("subject")}
            onClose={() => setActiveMarker(null)}
          />
        )}

        {saleComps.map((comp) => (
          <ComparableMarker
            key={`sale-${comp.id}`}
            position={{ lat: Number(comp.latitude), lng: Number(comp.longitude) }}
            property={comp}
            accentColor="#16a34a"
            dateLabel="Sold"
            isActive={activeMarker === `sale-${comp.id}`}
            onClick={() => setActiveMarker(`sale-${comp.id}`)}
            onClose={() => setActiveMarker(null)}
          />
        ))}

        {rentComps.map((comp) => (
          <ComparableMarker
            key={`rent-${comp.id}`}
            position={{ lat: Number(comp.latitude), lng: Number(comp.longitude) }}
            property={comp}
            accentColor="#f59e0b"
            dateLabel="Rented"
            isActive={activeMarker === `rent-${comp.id}`}
            onClick={() => setActiveMarker(`rent-${comp.id}`)}
            onClose={() => setActiveMarker(null)}
          />
        ))}
      </Map>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap", fontSize: 13 }}>
        {hasSubject && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 14, height: 14, borderRadius: 4, background: "#1a1a1a" }} />
            <span style={{ color: "#374151", fontWeight: 500 }}>Subject Property</span>
          </div>
        )}
        {saleComps.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 14, height: 14, borderRadius: 4, background: "#16a34a" }} />
            <span style={{ color: "#374151", fontWeight: 500 }}>Sale Comparables ({saleComps.length})</span>
          </div>
        )}
        {rentComps.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 14, height: 14, borderRadius: 4, background: "#f59e0b" }} />
            <span style={{ color: "#374151", fontWeight: 500 }}>Rental Comparables ({rentComps.length})</span>
          </div>
        )}
      </div>
    </APIProvider>
  );
}
